import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search as SearchIcon } from 'lucide-react';
import { ProductsService } from '../firebase';
import { Product } from '../types/product';
import OptimizedImage from '../components/OptimizedImage';

const PageContainer = styled.div`
  padding-top: var(--header-h);
  min-height: 70vh;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 40px 24px 80px;
`;

const SearchBar = styled.form`
  display: flex;
  gap: 0;
  max-width: 640px;
  margin: 0 auto 12px;
  border: 1px solid #1a1a1a;
  border-radius: 2px;
  overflow: hidden;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 14px 16px;
  border: none;
  font-size: 0.95rem;
  font-family: inherit;

  &:focus {
    outline: none;
  }
`;

const SearchButton = styled.button`
  padding: 0 24px;
  background: #1a1a1a;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #000;
  }
`;

const ResultInfo = styled.p`
  text-align: center;
  color: #777;
  font-size: 0.9rem;
  margin: 0 0 48px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 36px;
`;

const Card = styled.div`
  cursor: pointer;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

const ImageBox = styled.div`
  width: 100%;
  aspect-ratio: 3 / 4;
  overflow: hidden;
  background: #f4f4f4;
  margin-bottom: 14px;
`;

const Name = styled.h3`
  font-size: 0.98rem;
  font-weight: 400;
  color: #1a1a1a;
  margin: 0 0 6px 0;
`;

const Price = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: #1a1a1a;
`;

const OutOfStock = styled.span`
  display: inline-block;
  margin-left: 8px;
  font-size: 0.7rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #b3242f;
`;

const Empty = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;

  h2 {
    font-weight: 400;
    color: #1a1a1a;
    margin-bottom: 10px;
  }
`;

// Arama metnini karsilastirmaya hazirlar.
//
// Turkce harfler ASCII karsiliklarina indirgeniyor ki klavyesinde Turkce
// karakter kullanmayan biri de sonuc bulabilsin: "islemeli" yazan kisi
// "İşlemeli" urununu gormeli. Ayrica Turkce'de I/i donusumu ozel oldugu
// icin once locale'e duyarli kucuk harfe cevriliyor.
function normalize(text: string): string {
  return text
    .toLocaleLowerCase('tr')
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/â/g, 'a')
    .replace(/î/g, 'i')
    .replace(/û/g, 'u')
    .trim();
}

const categoryPath: Record<string, string> = {
  gelinlik: '/gelinlikler',
  abiye: '/abiyeler',
  kinalik: '/kinaliklar',
  aksesuar: '/aksesuar',
};

const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryText = searchParams.get('q') || '';

  const [input, setInput] = useState(queryText);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // Urunler bir KEZ cekilir, filtreleme bellekte yapilir.
  // Her tus vurusunda Firestore'a gitmek hem yavas olurdu hem de
  // gereksiz okuma maliyeti cikarirdi.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const response = await ProductsService.getProducts();
        const all: Product[] = response?.success && response.data ? response.data : [];
        if (!cancelled) {
          setAllProducts(all.filter((p: Product) => p.isActive !== false));
        }
      } catch (error) {
        console.error('Urunler yuklenemedi:', error);
        if (!cancelled) setAllProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Adres cubugundaki q degeri disaridan degisirse (geri/ileri tuslari,
  // paylasilan bir baglanti) kutuyu ona esitle.
  useEffect(() => {
    setInput(prev => (prev === queryText ? prev : queryText));
  }, [queryText]);

  // Yazma durduktan kisa sure sonra adres cubugunu guncelle.
  // Her harfte gecmise kayit dusmesin diye replace kullaniliyor.
  const firstSync = useRef(true);
  useEffect(() => {
    if (firstSync.current) {
      firstSync.current = false;
      return;
    }

    const timer = setTimeout(() => {
      const trimmed = input.trim();
      if (trimmed === queryText) return;
      setSearchParams(trimmed ? { q: trimmed } : {}, { replace: true });
    }, 350);

    return () => clearTimeout(timer);
  }, [input, queryText, setSearchParams]);

  const results = useMemo(() => {
    const term = normalize(input);
    if (!term) return [];

    return allProducts.filter(product => {
      const haystack = normalize(
        [
          product.name,
          product.shortDescription,
          product.detailedDescription,
          product.category,
          product.subcategory,
          ...(product.colors || []),
          ...(product.materials || []),
          ...(product.tags || []),
        ]
          .filter(Boolean)
          .join(' ')
      );

      return haystack.includes(term);
    });
  }, [input, allProducts]);

  const hasQuery = input.trim().length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(input.trim() ? { q: input.trim() } : {});
  };

  const openProduct = (product: Product) => {
    const base = categoryPath[product.category] || '/gelinlikler';
    navigate(`${base}/${product.slug}`);
  };

  return (
    <PageContainer>
      <Container>
        <SearchBar onSubmit={handleSubmit}>
          <SearchInput
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ürün, renk veya kumaş yazın — sonuçlar anında görünür"
            autoFocus
          />
          <SearchButton type="submit" aria-label="Ara">
            <SearchIcon size={20} />
          </SearchButton>
        </SearchBar>

        {loading ? (
          <ResultInfo>Ürünler yükleniyor...</ResultInfo>
        ) : hasQuery ? (
          <ResultInfo>
            "{input.trim()}" için {results.length} sonuç
          </ResultInfo>
        ) : (
          <ResultInfo>Aramak istediğiniz ürünü yazmaya başlayın</ResultInfo>
        )}

        {!loading && hasQuery && results.length === 0 && (
          <Empty>
            <h2>Sonuç bulunamadı</h2>
            <p>Farklı bir kelimeyle aramayı deneyebilir veya koleksiyonlarımıza göz atabilirsiniz.</p>
          </Empty>
        )}

        {results.length > 0 && (
          <Grid>
            {results.map(product => (
              <Card key={product.id} onClick={() => openProduct(product)}>
                <ImageBox>
                  <OptimizedImage
                    src={product.images?.[0] || '/placeholder.webp'}
                    alt={product.name}
                    fill
                    objectFit="cover"
                    sizes="(max-width: 768px) 50vw, 260px"
                  />
                </ImageBox>
                <Name>{product.name}</Name>
                <Price>
                  ₺{product.price?.toLocaleString('tr-TR')}
                  {product.stock === 0 && <OutOfStock>Stokta Yok</OutOfStock>}
                </Price>
              </Card>
            ))}
          </Grid>
        )}
      </Container>
    </PageContainer>
  );
};

export default SearchPage;
